import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { Badge, Button, EmptyState } from "@core/components";
import { CreateAppelDialog } from "../../create";
import { algiersParts } from "../format";

/** What the cohort page knows about one appel (the cohort's own answer, passed in by the route). */
export interface CohortAppelRow {
  id: string;
  code: string;
  name: string;
  status: string;
  opening_at: string | null;
  closing_at: string | null;
  submitted_count: number;
}

interface CohortAppelsProps {
  cohortId: string;
  appels: readonly CohortAppelRow[];
  /** The cohort says this account may create an appel (`available_actions.create_appel`). */
  canCreate: boolean;
  /** The name the server would give the next appel (« Appel 2 »). */
  nextName: string;
}

/**
 * The « Appels » section of a cohort page (Figma "Cohort, no appel" and "Create appel"): with no
 * appel, « Aucun appel pour l'instant » and « Créer un appel »; otherwise the appels of the cohort,
 * each a link to its first step, and the button above them. « Créer un appel » shows only when the
 * cohort allows it (not for an archived cohort, not for anyone without the right). The cohort page
 * composes this section; this module never reads the cohort itself.
 */
export function CohortAppels({
  cohortId,
  appels,
  canCreate,
  nextName,
}: CohortAppelsProps) {
  const { t } = useTranslation("appels");
  const navigate = useNavigate();
  const [isCreating, setCreating] = useState(false);

  const createButton = canCreate ? (
    <Button
      onPress={() => {
        setCreating(true);
      }}
    >
      <Icon name="plus" size={16} />
      {t("cohort.create")}
    </Button>
  ) : null;

  return (
    <section className="flex flex-col gap-sm">
      <div className="flex flex-wrap items-center justify-between gap-sm">
        <h2 className="text-md-semibold text-(--text-primary)">
          {t("cohort.title")}
        </h2>
        {appels.length > 0 ? createButton : null}
      </div>

      {appels.length === 0 ? (
        <EmptyState
          illustration="empty"
          title={t("cohort.empty")}
          className="min-h-75 justify-center"
          action={createButton}
        />
      ) : (
        <ul
          aria-label={t("cohort.title")}
          className="divide-y divide-(--border-default) overflow-hidden rounded-lg border border-(--border-default) bg-surface-card"
        >
          {appels.map((appel) => {
            const opening = appel.opening_at
              ? algiersParts(appel.opening_at)
              : null;
            const closing = appel.closing_at
              ? algiersParts(appel.closing_at)
              : null;
            return (
              <li key={appel.id}>
                <Link
                  to="/cohorts/$cohortId/appels/$appelId/programme"
                  params={{ cohortId, appelId: appel.id }}
                  className="flex items-center gap-md px-md py-sm outline-none hover:bg-ink-50 focus-visible:ring-2 focus-visible:ring-focus-ring"
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-xxs">
                    <span className="flex flex-wrap items-center gap-xs">
                      <span className="text-sm-medium text-(--text-primary)">
                        {appel.name}
                      </span>
                      <Badge outlined>{appel.code}</Badge>
                    </span>
                    {opening && closing ? (
                      <span className="text-xs-regular text-(--text-secondary)">
                        {opening.date} → {closing.date}
                      </span>
                    ) : null}
                  </span>
                  <Badge>
                    {appel.status === "DRAFT"
                      ? t("status.draft")
                      : appel.status}
                  </Badge>
                  <Icon
                    name="chevron-right"
                    size={18}
                    className="shrink-0 text-(--text-tertiary)"
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {isCreating ? (
        <CreateAppelDialog
          cohortId={cohortId}
          suggestedName={nextName}
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(appel) => {
            setCreating(false);
            void navigate({
              to: "/cohorts/$cohortId/appels/$appelId/programme",
              params: { cohortId, appelId: appel.id },
            });
          }}
        />
      ) : null}
    </section>
  );
}
